/**
 * Legal Compliance API Service
 *
 * Handles all legal compliance operations including:
 * - Document acceptance
 * - Digital signatures
 * - Compliance verification
 */

import {post} from './client';

const BASE_PATH = 'pegasus/visionary/legal';

export const legalComplianceApi = {
    /**
     * Save or update user's digital signature
     * @param {Object} params
     * @param {number} params.user_id - User ID (tenant_id or landlord_id)
     * @param {string} params.user_type - User type ('TENANT' or 'LANDLORD')
     * @param {string} params.signature_data - Base64 encoded signature image
     * @param {string} params.signature_format - Image format (default: 'PNG')
     * @returns {Promise<Object>} Signature save result with signature_id
     */
    saveSignature: async ({user_id, user_type, signature_data, signature_format = 'PNG'}) => {
        return post(`${BASE_PATH}/signature/save`, {
            user_id,
            user_type,
            signature_data,
            signature_format
        });
    },

    /**
     * Get user's saved signature
     * @param {Object} params
     * @param {number} params.user_id - User ID
     * @param {string} params.user_type - User type ('TENANT' or 'LANDLORD')
     * @returns {Promise<Object>} Signature data if exists
     */
    getSignature: async ({user_id, user_type}) => {
        return post(`${BASE_PATH}/signature/get`, {
            user_id,
            user_type
        });
    },

    /**
     * Accept legal documents
     * @param {Object} params
     * @param {number} params.user_id - User ID
     * @param {string} params.user_type - User type ('TENANT' or 'LANDLORD')
     * @param {Array<Object>} params.document_ids - Array of {document_id: number}
     * @param {number} params.signature_id - Signature ID from saveSignature
     * @param {string} params.acceptance_method - Method of acceptance (default: 'WEB')
     * @returns {Promise<Object>} Acceptance result
     */
    acceptDocuments: async ({
                                user_id,
                                user_type,
                                document_ids,
                                signature_id,
                                acceptance_method = 'WEB'
                            }) => {
        return post(`${BASE_PATH}/accept`, {
            user_id,
            user_type,
            document_ids,
            signature_id,
            acceptance_method
        });
    },

    /**
     * Get current legal documents
     * @returns {Promise<Object>} List of current legal documents
     */
    getCurrentDocuments: async () => {
        return post(`${BASE_PATH}/documents/current`, {});
    }
};

export default legalComplianceApi;
